import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Space, Typography } from 'antd';

import { showMessage, showNotification } from 'utils/notificationUtil';
import {
    getMSGraphAuthURLIntegrationAccess,
    getMyIntegrationCredentials,
    generateLogoutCallbackURL,
    logoutByProvider,
} from 'utils/ez_api/integrationCredentialAPIs';
import {
    IntegrationCredentialType,
    IntegrationServiceTypeEnum,
    IntegrationTypeEnum,
} from 'types/integrationCredential';
import { ReloadOutlined } from '@ant-design/icons';

type MicrosoftCalendarLoginButtonProps = {
    children?: React.ReactNode;
};

const { Title, Text } = Typography;

export const MicrosoftCalendarLoginSection: React.FC<MicrosoftCalendarLoginButtonProps> = ({
    children,
}) => {
    const [microsoftCredential, setMicrosoftCredential] =
        useState<IntegrationCredentialType | null>(null);
    const [isMS265ButtonClicked, setIsMS265ButtonClicked] = useState<boolean>(false);

    const startMicrosoftAuth = () => {
        setIsMS265ButtonClicked(true);
        getMSGraphAuthURLIntegrationAccess(IntegrationServiceTypeEnum.CALENDAR)
            .then(resp => {
                const url = resp.data?.url;
                if (url) {
                    window.open(url, '_blank');
                } else {
                    showMessage('error', 'Could not initiate Microsoft OAuth.');
                }
            })
            .catch(() => showMessage('error', 'Failed to get Microsoft login URL.'));
    };

    const _fetchMyMicrosoftCredentials = () => {
        getMyIntegrationCredentials(IntegrationTypeEnum.MICROSOFT).then(resp => {
            const credentials = resp.data as IntegrationCredentialType[];
            if (credentials.length > 0) {
                const activeMicrosoftCredential = credentials.find(
                    credential => credential.is_calendar_enabled && credential.email
                );
                setMicrosoftCredential(activeMicrosoftCredential ?? null);
            } else {
                setMicrosoftCredential(null);
            }

            setIsMS265ButtonClicked(false);
        });
    };

    const _onMicrosoftLogout = () => {
        setIsMS265ButtonClicked(true);
        // generateLogoutCallbackURL(IntegrationTypeEnum.MICROSOFT, microsoftCredential?.email).then(
        //     resp => {
        //         const url = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
        //             resp.data?.url
        //         )}`;
        //         window.open(url, '_blank');
        //     }
        // );
        logoutByProvider(
            IntegrationTypeEnum.MICROSOFT,
            microsoftCredential?.email ?? '',
            IntegrationServiceTypeEnum.CALENDAR
        ).then(() => {
            setMicrosoftCredential(null);
            showNotification('success', 'You have unlinked a Microsoft account successfully.');
        });
    };

    useEffect(() => {
        _fetchMyMicrosoftCredentials();
    }, []);

    return (
        <Card>
            <Space direction="vertical" size="small">
                <Title level={4} className="mb-0">
                    Microsoft 365 Calendar
                </Title>
                <Text type="secondary">
                    Start Microsoft OAuth to allow calendar access via Graph.
                </Text>
                <Button type="primary" onClick={startMicrosoftAuth}>
                    {microsoftCredential && microsoftCredential.email
                        ? 'Connected as ' + microsoftCredential.email
                        : 'Sign in with Microsoft 365'}
                </Button>

                {microsoftCredential && microsoftCredential.email && (
                    <Button
                        // type="link"
                        onClick={() => {
                            Modal.confirm({
                                title: 'Disconnect Microsoft 365',
                                content: (
                                    <div>
                                        <p>
                                            Are you sure you want to disconnect your Microsoft 365?
                                        </p>
                                        <p>
                                            If you use the same Microsoft account for your linked
                                            email, you will need to re-authorize to connect again.
                                        </p>
                                    </div>
                                ),
                                onOk: () => {
                                    _onMicrosoftLogout();
                                },
                            });
                        }}
                    >
                        Disconnect
                    </Button>
                )}

                {isMS265ButtonClicked && (
                    <a onClick={() => _fetchMyMicrosoftCredentials()}>
                        <u>
                            <ReloadOutlined /> Reload Integration
                        </u>
                    </a>
                )}
            </Space>
        </Card>
    );
};

export default MicrosoftCalendarLoginSection;
